import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Edit, Trash2, Play, Image, Video, Globe, Lock, Crown, Eye, Copy, FileText, User, Calendar, Tag, Clock, Sparkles } from 'lucide-react'
import { formatOccasionDisplay, isOccasionActive } from '../../utils/occasionUtils'
import { toUtcDate } from '../../utils/dateFormat'

const EnhancedTemplateCard = ({ 
  template, 
  size = 'normal', 
  showActions = true,
  onEdit,
  onDelete,
  onClone,
  onUse,
  onGenerateSample,
  actionLoading = {},
  isSelected = false,
  onSelect,
  showBulkActions = false
}) => {
  const navigate = useNavigate()
  const [showTooltip, setShowTooltip] = useState(false)
  const [showCopiedTooltip, setShowCopiedTooltip] = useState(false)
  
  const isVideo = template.type === 'video' || template.mediaType === 'video'
  const previewUrl = template.thumbnailUrl || template.sampleImageUrl || template.thumbnail || template.previewImage || template.sampleImage
  
  // Format ID to show first 8 characters
  const shortId = template.id ? template.id.toString().substring(0, 8) : 'N/A'
  
  // Get category and subcategory names
  const categoryName = String(template.Category?.name || template.category?.name || template.category || 'General')
  const subcategoryName = template.SubCategory?.name || template.Subcategory?.name || template.subcategory?.name || template.sub_category?.name || (template.subcategory ? String(template.subcategory) : null) || (template.sub_category ? String(template.sub_category) : null)
  
  // Get owner name
  const ownerName = template.User?.name || template.user?.name || template.ownerName || 'Unknown'
  
  // Format occasion dates
  const formatDate = (dateString) => {
    if (!dateString) return null
    try {
      // toUtcDate treats timezone-less backend timestamps as UTC so IST is correct.
      return toUtcDate(dateString).toLocaleDateString('en-IN', { 
        timeZone: 'Asia/Kolkata',
        month: 'short', 
        day: 'numeric',
        year: 'numeric'
      })
    } catch {
      return dateString
    }
  }
  
  // Format datetime for display
  const formatDateTime = (dateString) => {
    if (!dateString) return null
    try {
      return toUtcDate(dateString).toLocaleString('en-IN', {
        timeZone: 'Asia/Kolkata',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      })
    } catch {
      return dateString
    }
  }
  
  const occasionStartDate = formatDate(template.occasionStartDate)
  const occasionEndDate = formatDate(template.occasionEndDate)
  const createdAt = formatDateTime(template.createdAt)
  const updatedAt = formatDateTime(template.updatedAt)
  
  const handleCardClick = (e) => {
    // Only handle selection in bulk actions mode
    // Don't open template on card click - only "Use Template" button should open it
    if (showBulkActions && e.target.type !== 'checkbox') {
      e.preventDefault()
      onSelect && onSelect(template.id)
    }
    // Removed the else clause that was opening template on card click
  }

  const handleCopyId = async (e) => {
    e.stopPropagation()
    try {
      await navigator.clipboard.writeText(template.id)
      setShowCopiedTooltip(true)
      setTimeout(() => {
        setShowCopiedTooltip(false)
      }, 2000)
    } catch (err) {
      console.error('Failed to copy ID:', err)
    }
  }

  return (
    <div
      className={`group cursor-pointer ${
        size === 'large' ? 'col-span-2 row-span-2' : ''
      } ${isSelected ? 'ring-2 ring-blue-500' : ''}`}
      onClick={handleCardClick}
    >
      <div className="relative overflow-hidden rounded-xl bg-white shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100">
        {/* Template Preview */}
        <div className={`relative ${
          size === 'large' ? 'aspect-[4/3]' : 'aspect-[3/4]'
        } bg-gradient-to-br from-gray-50 to-gray-100 overflow-hidden`}>

          {/* Image/Video Preview */}
          {previewUrl ? (
            <>
              {isVideo && template.sampleVideo ? (
                <video
                  className="w-full h-full object-contain bg-gray-100"
                  muted
                  loop
                  playsInline
                  onMouseEnter={(e) => e.target.play()}
                  onMouseLeave={(e) => e.target.pause()}
                >
                  <source src={template.sampleVideo} type="video/mp4" />
                  <img
                    src={previewUrl}
                    alt={template.title || template.name}
                    className="w-full h-full object-contain bg-gray-100"
                  />
                </video>
              ) : (
                <img
                  src={previewUrl}
                  alt={template.title || template.name}
                  className="w-full h-full object-contain bg-gray-100"
                  onError={(e) => {
                    e.target.style.display = 'none'
                    e.target.nextSibling.style.display = 'flex'
                  }}
                />
              )}
              {/* Fallback for failed image */}
              <div className="absolute inset-0 bg-gradient-to-br from-gray-200 to-gray-300 hidden items-center justify-center">
                <div className="text-center text-gray-500">
                  <Image className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p className="text-sm font-medium">Preview not available</p>
                </div>
              </div>
            </>
          ) : (
            <div className="absolute inset-0 bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center">
              <div className="text-center text-gray-500">
                <Image className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p className="text-sm font-medium">Preview not available</p>
              </div>
            </div>
          )}
        
          {/* Video Play Button */}
          {isVideo && previewUrl && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-16 h-16 bg-black/60 rounded-full flex items-center justify-center backdrop-blur-sm group-hover:bg-black/70 transition-colors">
                <Play className="w-8 h-8 text-white ml-1" fill="currentColor" />
              </div>
            </div>
          )}

          {/* Top Left - Selection Checkbox or Design Type Badge */}
          <div className="absolute top-3 left-3">
            {showBulkActions ? (
              <input
                type="checkbox"
                checked={isSelected}
                onChange={(e) => {
                  e.stopPropagation()
                  onSelect && onSelect(template.id)
                }}
                className="w-5 h-5 text-blue-600 bg-white border-2 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
              />
            ) : (
              <div className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium ${
                isVideo
                  ? 'bg-red-500/90 text-white'
                  : 'bg-blue-500/90 text-white'
              }`}>
                {isVideo ? <Video className="w-3 h-3" /> : <Image className="w-3 h-3" />}
                <span>{isVideo ? 'Video' : 'Image'}</span>
              </div>
            )}
          </div>

          {/* Top Right - Status Icons */}
          <div className="absolute top-3 right-3 flex flex-col gap-2 items-end">
            {/* Premium Badge */}
            {(template.isPremium || template.isPro) && (
              <div className="flex items-center gap-1 px-2 py-1 bg-amber-500/90 text-white rounded-md text-xs font-medium">
                <Crown className="w-3 h-3" />
                <span>Premium</span>
              </div>
            )}

            {/* Public/Private Status */}
            <div className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium ${
              template.is_public !== false
                ? 'bg-green-500/90 text-white'
                : 'bg-gray-500/90 text-white'
            }`}>
              {template.is_public !== false ? (
                <>
                  <Globe className="w-3 h-3" />
                  <span>Public</span>
                </>
              ) : (
                <>
                  <Lock className="w-3 h-3" />
                  <span>Private</span>
                </>
              )}
            </div>

            {/* Stage Badge */}
            {template.stage && (
              <div className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium ${
                template.stage === 'approved' ? 'bg-green-500/90 text-white' :
                template.stage === 'submitted' ? 'bg-yellow-500/90 text-white' :
                template.stage === 'rejected' ? 'bg-red-500/90 text-white' :
                'bg-gray-500/90 text-white'
              }`}>
                <FileText className="w-3 h-3" />
                <span>{template.stage.charAt(0).toUpperCase() + template.stage.slice(1)}</span>
              </div>
            )}
          </div>

          {/* Hover Actions */}
          {showActions && !showBulkActions && (
            <div className="absolute inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300">
              <div className="flex flex-col space-y-2">
                <button
                  className="px-6 py-2 bg-white text-gray-900 rounded-lg font-semibold hover:bg-gray-100 transition-colors shadow-lg"
                  onClick={(e) => {
                    e.stopPropagation()
                    onUse && onUse(template)
                  }}
                >
                  Use Template
                </button>
                <div className="flex space-x-2 justify-center">
                  {onEdit && !template.is_public && !template.is_public && (
                    <button
                      className="p-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors shadow-lg"
                      onClick={(e) => {
                        e.stopPropagation()
                        onEdit(template)
                      }}
                      title="Edit template"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                  )}
                  {onEdit && (template.is_public || template.is_public) && (
                    <button
                      className="p-2 bg-gray-400 text-white rounded-lg cursor-not-allowed shadow-lg"
                      onClick={(e) => e.stopPropagation()}
                      title="Public templates cannot be edited"
                      disabled
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                  )}

                  {onClone && (
                    <button
                      className="p-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors shadow-lg"
                      onClick={(e) => {
                        e.stopPropagation()
                        onClone(template)
                      }}
                      disabled={actionLoading[template.id] === 'cloning'}
                      title="Clone template"
                    >
                      {actionLoading[template.id] === 'cloning' ? (
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </button>
                  )}

                  {onDelete && (
                    <button
                      className="p-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors shadow-lg"
                      onClick={(e) => {
                        e.stopPropagation()
                        onDelete(template)
                      }}
                      title="Delete template"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}

                  {onGenerateSample && (
                    <button
                      className="p-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors shadow-lg"
                      onClick={(e) => {
                        e.stopPropagation()
                        onGenerateSample(template)
                      }}
                      disabled={actionLoading[template.id] === 'generating'}
                      title="Generate sample"
                    >
                      {actionLoading[template.id] === 'generating' ? (
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <Sparkles className="w-4 h-4" />
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Enhanced Template Info */}
        <div className="p-4 space-y-3">
          {/* ID and Name */}
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <button
                  onClick={handleCopyId}
                  className="relative text-xs text-gray-500 font-mono bg-gray-100 px-2 py-1 rounded hover:bg-gray-200 hover:text-gray-700 transition-colors cursor-pointer flex items-center gap-1 group"
                  title="Click to copy full ID"
                >
                  <span>ID: {shortId}</span>
                  <Copy className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                  
                  {/* Copied Tooltip */}
                  {showCopiedTooltip && (
                    <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-green-600 text-white text-xs px-2 py-1 rounded whitespace-nowrap shadow-lg">
                      Copied!
                      <span className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-green-600"></span>
                    </span>
                  )}
                </button>
              </div>
              <h3 className="font-semibold text-gray-900 text-sm leading-tight line-clamp-2">
                {template.title || template.name}
              </h3>
            </div>
            {/* Usage count */}
            {template.usageCount > 0 && (
              <div className="flex items-center text-xs text-gray-400 ml-2">
                <Eye className="w-3 h-3 mr-1" />
                <span>{template.usageCount}</span>
              </div>
            )}
          </div>

          {/* Category and Subcategory */}
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <Tag className="w-3 h-3 text-gray-400" />
              <span className="text-xs text-gray-600">
                <span className="font-medium">{categoryName}</span>
                {subcategoryName && (
                  <span className="text-gray-500"> • {subcategoryName}</span>
                )}
              </span>
            </div>
          </div>

          {/* Occasion Information */}
          {template.occasionType && (
            <div className="space-y-1">
              <div className={`text-xs px-2 py-1 rounded-md ${
                isOccasionActive(template)
                  ? 'bg-green-50 text-green-700 border border-green-200'
                  : 'bg-blue-50 text-blue-700 border border-blue-200'
              }`}>
                <div className="font-medium">{template.occasionType}</div>
                {(occasionStartDate || occasionEndDate) && (
                  <div className="flex items-center gap-1 mt-1">
                    <Calendar className="w-3 h-3" />
                    <span>
                      {occasionStartDate && occasionEndDate 
                        ? `${occasionStartDate} - ${occasionEndDate}`
                        : occasionStartDate || occasionEndDate
                      }
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Designer Information */}
          <div className="flex items-center gap-2">
            <User className="w-3 h-3 text-gray-400" />
            <span className="text-xs text-gray-600">
              Designer: <span className="font-medium">{ownerName}</span>
            </span>
          </div>

          {/* Created Date with Hover Tooltip */}
          {createdAt && (
            <div className="relative">
              <div 
                className="flex items-center gap-2 cursor-help"
                onMouseEnter={() => setShowTooltip(true)}
                onMouseLeave={() => setShowTooltip(false)}
              >
                <Clock className="w-3 h-3 text-gray-400" />
                <span className="text-xs text-gray-600">
                  Created: <span className="font-medium">{createdAt}</span>
                </span>
              </div>
              
              {/* Tooltip */}
              {showTooltip && (
                <div className="absolute bottom-full left-0 mb-2 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-lg z-10 min-w-max">
                  <div className="space-y-1">
                    <div><strong>Created:</strong> {createdAt}</div>
                    {updatedAt && <div><strong>Updated:</strong> {updatedAt}</div>}
                  </div>
                  {/* Tooltip arrow */}
                  <div className="absolute top-full left-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                </div>
              )}
            </div>
          )}

          {/* Status Indicators */}
          <div className="flex items-center justify-between pt-2 border-t border-gray-100">
            <div className="flex items-center gap-2">
              {template.is_public !== false && (
                <div className="w-2 h-2 bg-green-400 rounded-full" title="Public template" />
              )}
              {(template.isPremium || template.isPro) && (
                <div className="w-2 h-2 bg-amber-400 rounded-full" title="Premium template" />
              )}
            </div>
            <div className="text-xs text-gray-500">
              {template.stage && (
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  template.stage === 'approved' ? 'bg-green-100 text-green-800' :
                  template.stage === 'submitted' ? 'bg-yellow-100 text-yellow-800' :
                  template.stage === 'rejected' ? 'bg-red-100 text-red-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {template.stage}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default EnhancedTemplateCard
